import AHBoolean from "common/values/boolean/boolean";
import AHDate from "common/values/date/date";
import Percent from "common/values/percent/percent";
import Individual from "marketplace/entities/individual/individual";
import EntityClientRepresentative from "work/entities/entity-client-representative/entity-client-representative";
import Proposal, { ProposalField, ProposalFieldCategory } from "work/entities/proposal/proposal";
import { FeeScheduleRedline } from "work/entities/proposal/redlining/fee-schedule-redline/fee-schedule-redline";
import FieldRedline, { FieldRedlineArray, } from "work/entities/proposal/redlining/field-redline";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import { ProposalFieldName } from "work/values/constants";
import FeeScheduleCategory, {
  FeeScheduleCategoryMaskedCurrency
} from "work/values/fee-schedule-category/fee-schedule-category";
import ProjectDescription from "work/values/project-description/project-description";
import ProjectName from "work/values/project-name/project-name";
import WorkDocument from "work/values/work-document/work-document";
import DetailedVendorTeam from "../vendor-team/detailed-vendor-team";
import {
  Masked,
  MaskedContracts,
  MaskedDiscount,
  MaskedFeeSchedule,
  MaskedTeam
} from "../../entities/work-agreement/work-agreement";

export default class ProposalIssues {
  private _entries: ProposalIssue[] = [];

  public get entries(): ProposalIssue[] {
    return [...this._entries];
  }

  public get canSubmit(): boolean {
    return this.canSave &&
      !this._entries.some((entry) => entry.field.isEqualTo(ProposalField.TeamLeader))
  }

  public get canSave(): boolean {
    return !this._entries.filter((entry) => !entry.field.isEqualTo(ProposalField.TeamLeader))
      .some((entry) => entry.level === ProposalIssueLevel.Critical)
  }

  public static fromProposal(proposal: Proposal): ProposalIssues {
    if (!(proposal.vendorTeam instanceof DetailedVendorTeam)) {
      console.warn("Proposal is not detailed.")
      return new ProposalIssues();
    }
    const issues = new ProposalIssues();
    issues.validateClient(proposal.client);
    issues.validateName(proposal.name);
    issues.validateDescription(proposal.description);
    issues.validateDates(
      proposal.responseDueBy,
      proposal.startDate,
      proposal.endDate
    );
    issues.validateTeam(proposal?.vendorTeam);
    issues.validateFeeSchedule(proposal.feeSchedule ?? []);
    issues.validateConflictsDocuments(
      proposal.conflictsCheckWaived ?? new AHBoolean(false),
      proposal.conflictsDocuments ?? []
    );
    return issues;
  }

  public static fromBuilder(builder?: ProposalBuilder): ProposalIssues {
    const issues = new ProposalIssues();
    if (!builder) {
      return issues;
    }

    issues.validateClient(builder.currentSpec.client);
    issues.validateName(builder.currentSpec.name);
    issues.validateDescription(builder.currentSpec.description);
    issues.validateDates(
      builder.currentSpec.responseDueBy,
      builder.currentSpec.startDate,
      builder.currentSpec.endDate
    );
    issues.validateTeam(builder.currentSpec.team);
    issues.validateClientTeamLeaderRole(
      builder.currentSpec.client,
      builder.currentSpec.team
    );

    if (Masked.isNotMasked(builder.currentSpec.feeSchedule)) {
      issues.validateFeeSchedule(
        builder.currentSpec.feeSchedule ?? []
      );
    }

    issues.validateConflictsDocuments(
      builder.currentSpec.conflictsCheckWaived ??
      new AHBoolean(false),
      builder.currentSpec.conflictsDocuments ?? []
    );

    return issues;
  }

  public static fromRedline(redline: ProposalRedline): ProposalIssues {
    const issues = new ProposalIssues();

    issues.validateNameRedline(redline.name);
    issues.validateDescriptionRedline(redline.description);
    issues.validateDatesRedline(
      redline.responseDueBy,
      redline.startDate,
      redline.endDate
    );
    issues.validateTeamRedline(
      redline.teamRestricted,
      redline.team
    );
    issues.validateFeeScheduleRedline(redline.feeSchedule);
    issues.validateConflictsDocumentsRedline(
      redline.conflictsCheckWaived,
      redline.conflictsDocuments
    );
    issues.validatePolicyDocumentsRedline(redline.clientPolicyDocuments);
    issues.validatePolicyDocumentsRedline(redline.vendorPolicyDocuments);
    issues.validateDiscountRedline(redline.discount);
    return issues;
  }

  private validateClient(client?: EntityClientRepresentative | null): void {
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Client
    );
    if (!client) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Client,
          "Client is a required field.",
          ProposalIssueLevel.Critical
        )
      );
    }
  }

  private validateClientTeamLeaderRole(
    client: EntityClientRepresentative | undefined | null,
    team: DetailedVendorTeam | MaskedTeam | undefined | null
  ) {
    if (Masked.isMasked(team)) return;
    if (client?.userId && team?.leader?.userId) {
      if (client.userId.isEqualTo(team.leader.userId)) {
        this._entries.push(
          new ProposalIssue(
            ProposalField.Client,
            "Client cannot be the same as vendor-team leader.",
            ProposalIssueLevel.Critical
          )
        );
        this._entries.push(
          new ProposalIssue(
            ProposalField.TeamLeader,
            "Client cannot be the same as vendor-team leader.",
            ProposalIssueLevel.Critical
          )
        );
      }
    }
  }

  private validateName(name?: ProjectName | null): void {
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Name
    );
    if (!name || name.value === "") {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Name,
          "Name is a required field.",
          ProposalIssueLevel.Critical
        )
      );
    }
  }

  private validateNameRedline(nameRedline: FieldRedline<ProjectName>): void {
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Name
    );

    if (!nameRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Name,
          "Changes requested to name.",
          ProposalIssueLevel.Critical
        )
      );
    } else {
      this.validateName(nameRedline.currentEntry);
    }
  }

  private validateDescription(description?: ProjectDescription | null): void {
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Description
    );
    if (!description || description.value === "") {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Description,
          "Description is a required field.",
          ProposalIssueLevel.Critical
        )
      );
    }
  }

  private validateDescriptionRedline(
    descriptionRedline: FieldRedline<ProjectDescription>
  ): void {
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Description
    );

    if (!descriptionRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Description,
          "Changes requested to description.",
          ProposalIssueLevel.Critical
        )
      );
    } else {
      this.validateDescription(descriptionRedline.currentEntry);
    }
  }

  private validateDates(
    responseDueBy?: AHDate | null,
    startDate?: AHDate | null,
    endDate?: AHDate | null
  ): void {
    this._entries = this._entries.filter(
      (entry) =>
        [
          ProposalFieldName.ResponseDueBy,
          ProposalFieldName.StartDate,
          ProposalFieldName.EndDate,
        ].indexOf(entry.field.name) === -1
    );
    const now = new AHDate();

    if (responseDueBy && !responseDueBy.isValid) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Response due by date is an invalid date.",
          ProposalIssueLevel.Critical
        )
      );
    }
    if (responseDueBy && responseDueBy.isValid && now.isAfter(
      responseDueBy,
      'day'
    )) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Response due by cannot be in the past.",
          ProposalIssueLevel.Critical
        )
      );
    }

    if (startDate && !startDate.isValid) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.StartDate,
          "Start date is an invalid date.",
          ProposalIssueLevel.Critical
        )
      );
    }
    if (startDate && startDate.isValid && now.isAfter(
      startDate,
      'day'
    )) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.StartDate,
          "Start date cannot be in the past.",
          ProposalIssueLevel.Critical
        )
      );
    }

    if (endDate && !endDate.isValid) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.EndDate,
          "End date is an invalid date.",
          ProposalIssueLevel.Critical
        )
      );
    }
    if (endDate && endDate.isValid && now.isAfter(
      endDate,
      'day'
    )) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.EndDate,
          "End date cannot be in the past.",
          ProposalIssueLevel.Critical
        )
      );
    }
    if (responseDueBy?.isValid && startDate?.isValid && responseDueBy.isAfter(
      startDate,
      'day'
    )) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Response due by date cannot be after start date.",
          ProposalIssueLevel.Critical
        )
      );
    }
    if (startDate?.isValid && endDate?.isValid && startDate.isAfter(
      endDate,
      'day'
    )) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.StartDate,
          "Start date cannot be after end date.",
          ProposalIssueLevel.Critical
        )
      );
    }
  }

  private validateDatesRedline(
    responseDueByRedline: FieldRedline<AHDate>,
    startDateRedline: FieldRedline<AHDate>,
    endDateRedline: FieldRedline<AHDate>
  ): void {
    this._entries = this._entries.filter(
      (entry) =>
        [
          ProposalFieldName.ResponseDueBy,
          ProposalFieldName.StartDate,
          ProposalFieldName.EndDate,
        ].indexOf(entry.field.name) === -1
    );

    if (!responseDueByRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Changes requested to response due by date.",
          ProposalIssueLevel.Critical
        )
      );
    }

    if (!startDateRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.StartDate,
          "Changes requested to start date.",
          ProposalIssueLevel.Critical
        )
      );
    }

    if (!endDateRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.EndDate,
          "Changes requested to end date.",
          ProposalIssueLevel.Critical
        )
      );
    }

    if (
      responseDueByRedline.isResolved &&
      startDateRedline.isResolved &&
      endDateRedline.isResolved
    ) {
      this.validateDates(
        responseDueByRedline.currentEntry,
        startDateRedline.currentEntry,
        endDateRedline.currentEntry
      );
    }
  }

  private validateTeam(team?: DetailedVendorTeam | MaskedTeam | null): void {
    if (Masked.isMasked(team)) return;

    this._entries = this._entries.filter(
      (entry) => entry.field.category !== ProposalFieldCategory.Team
    );
    if (!team?.leader) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.TeamLeader,
          "Team leader is required in order to submit proposal.",
          ProposalIssueLevel.Critical
        )
      );
    }
  }

  private validateTeamRedline(
    teamRestrictedRedline: FieldRedline<AHBoolean> | MaskedTeam,
    teamRedline: FieldRedlineArray<Individual> | MaskedTeam
  ): void {
    if (Masked.isMasked(teamRestrictedRedline)) return;
    if (Masked.isMasked(teamRedline)) return;
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Team
    );

    if (!teamRestrictedRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.TeamRestriction,
          "Changes requested to vendor-team restriction.",
          ProposalIssueLevel.Critical
        )
      );
    }

    for (const memberRedline of teamRedline.redlines) {
      if (!memberRedline.isResolved) {
        this._entries.push(
          new ProposalIssue(
            memberRedline.field,
            `Changes requested for team member: ${memberRedline.revisedEntry?.getFullName() ??
            memberRedline.originalEntry?.getFullName()
            }.`,
            ProposalIssueLevel.Critical
          )
        );
      }
    }
  }

  private validateFeeSchedule(feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule): void {
    if (Masked.isMasked(feeSchedule)) return;
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.FeeSchedule
    );
    if (feeSchedule.length === 0) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.FeeSchedule,
          "Fee schedule is required in order to hire proposal.",
          ProposalIssueLevel.Warning
        )
      );
      return;
    }
    for (const deferredCategory of feeSchedule.filter(
      (category) =>
        category instanceof FeeScheduleCategory &&
        !category.fee
    )) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.FeeScheduleCategory(deferredCategory.id),
          `Deferred fee for ${deferredCategory.name} must be set to a valid
           amount before the proposal can be hired.`,
          ProposalIssueLevel.Warning
        )
      );
    }
  }

  private validateFeeScheduleRedline(
    feeScheduleRedline: FeeScheduleRedline | MaskedFeeSchedule
  ): void {
    if (Masked.isMasked(feeScheduleRedline)) return;
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.FeeSchedule
    );

    for (const categoryRedline of feeScheduleRedline.redlines) {
      if (!categoryRedline.isResolved) {
        this._entries.push(
          new ProposalIssue(
            categoryRedline.field,
            `Changes requested to fee schedule category: ${categoryRedline.revisedEntry?.name ??
            categoryRedline.originalEntry?.name
            }.`,
            ProposalIssueLevel.Critical
          )
        );
      } else if (
        categoryRedline.currentEntry instanceof FeeScheduleCategory &&
        categoryRedline.currentEntry &&
        !categoryRedline.currentEntry.fee
      ) {
        this._entries.push(
          new ProposalIssue(
            categoryRedline.field,
            `Deferred fee for ${categoryRedline.currentEntry.name} must be set to a valid
             amount before the proposal can be hired.`,
            ProposalIssueLevel.Warning
          )
        );
      }
    }
    if (
      feeScheduleRedline.redlines.length < 1 ||
      (feeScheduleRedline.isResolved &&
        feeScheduleRedline.redlines.filter((redline) => redline.currentEntry)
          .length < 1)
    ) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.FeeSchedule,
          "Fee schedule is required in order to hire proposal.",
          ProposalIssueLevel.Warning
        )
      );
    }
  }

  private validateConflictsDocuments(
    conflictsCheckWaived: AHBoolean | MaskedContracts,
    documents: WorkDocument[] | MaskedContracts
  ) {
    if (Masked.isMasked(conflictsCheckWaived)) return;
    if (Masked.isMasked(documents)) return;
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Conflicts
    );
    if (!conflictsCheckWaived.value && documents.length === 0) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Conflicts,
          "Conflicts document(s) are required in order to hire when conflicts check is not waived.",
          ProposalIssueLevel.Warning
        )
      );
    }
  }

  private validateConflictsDocumentsRedline(
    conflictsCheckWaived: FieldRedline<AHBoolean> | MaskedContracts,
    documentsRedline: FieldRedlineArray<WorkDocument> | MaskedContracts
  ) {
    if (Masked.isMasked(conflictsCheckWaived)) return;
    if (Masked.isMasked(documentsRedline)) return;
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Conflicts
    );

    if (!conflictsCheckWaived.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.WaiveConflictsCheck,
          "Changes requested to conflicts check waiver.",
          ProposalIssueLevel.Critical
        )
      );
    }

    for (const documentRedline of documentsRedline.redlines) {
      if (!documentRedline.isResolved) {
        this._entries.push(
          new ProposalIssue(
            documentRedline.field,
            "Changes requested to conflicts document(s).",
            ProposalIssueLevel.Critical
          )
        );
      }
    }

    if (
      conflictsCheckWaived.isResolved &&
      !conflictsCheckWaived.currentEntry?.value &&
      documentsRedline.redlines.filter((redline) => redline.currentEntry)
        .length < 1
    ) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Conflicts,
          "Conflicts document(s) are required in order to hire when conflicts check is not waived.",
          ProposalIssueLevel.Warning
        )
      );
    }
  }

  private validatePolicyDocumentsRedline(
    documentsRedline: FieldRedlineArray<WorkDocument> | MaskedContracts
  ) {
    if (Masked.isMasked(documentsRedline)) return;
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== documentsRedline.field.name
    );

    for (const documentRedline of documentsRedline.redlines) {
      if (!documentRedline.isResolved && documentRedline.field.id) {
        this._entries.push(
          new ProposalIssue(
            documentRedline.field,
            "Changes requested to policy document.",
            ProposalIssueLevel.Critical
          )
        );
      }
    }
  }

  private validateDiscountRedline(discountRedline: FieldRedline<Percent> | MaskedDiscount): void {
    if (Masked.isMasked(discountRedline)) return;
    this._entries = this._entries.filter(
      (entry) => entry.field.name !== ProposalFieldName.Discount
    );

    if (!discountRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Discount,
          "Changes requested to discount.",
          ProposalIssueLevel.Critical
        )
      );
    }
  }
}

export class ProposalIssue {
  public readonly field: ProposalField;
  public readonly description: string;
  public readonly level: ProposalIssueLevel;

  constructor(
    field: ProposalField,
    description: string,
    level: ProposalIssueLevel
  ) {
    this.field = field;
    this.description = description;
    this.level = level;
  }
}

export enum ProposalIssueLevel {
  Critical,
  Warning,
  Info,
}
